import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";
import {
  createVideoNewsChannel,
  deleteVideoNewsChannel,
  getVideoNewsChannels,
  syncVideoNewsNow,
  updateVideoNewsChannel,
} from "../api/videoNews";
import toast from "react-hot-toast";
import {
  Plus,
  Trash2,
  Loader2,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Youtube,
  Pencil,
  X,
  Check,
} from "lucide-react";

const EMPTY_FORM = {
  name: "",
  channelId: "",
  handle: "",
  channelUrl: "",
  category: "Other",
  language: "en",
  priority: 5,
  isActive: true,
};

const VideoNewsDashboard = () => {
  const { adminToken } = useAuth();
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [videoCount, setVideoCount] = useState(0);
  const [scheduler, setScheduler] = useState(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const fetchChannels = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVideoNewsChannels(adminToken);
      if (data.success) {
        setChannels(data.channels || []);
        setCategories(data.categories || []);
        setVideoCount(data.videoCount || 0);
        setScheduler(data.scheduler || null);
        setHasApiKey(Boolean(data.hasYoutubeApiKey));
      }
    } catch (error) {
      toast.error("Failed to load video channels");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) fetchChannels();
  }, [adminToken, fetchChannels]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (channel) => {
    setForm({
      name: channel.name || "",
      channelId: channel.channelId || "",
      handle: channel.handle || "",
      channelUrl: channel.channelUrl || "",
      category: channel.category || "Other",
      language: channel.language || "en",
      priority: channel.priority || 5,
      isActive: channel.isActive !== false,
    });
    setEditingId(channel._id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingId) {
        await updateVideoNewsChannel(editingId, form, adminToken);
        toast.success("Channel updated");
      } else {
        await createVideoNewsChannel(form, adminToken);
        toast.success("Channel added");
      }
      resetForm();
      fetchChannels();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (channel) => {
    try {
      await updateVideoNewsChannel(channel._id, { isActive: !channel.isActive }, adminToken);
      fetchChannels();
    } catch {
      toast.error("Failed to toggle channel");
    }
  };

  const handleDelete = async (channel) => {
    if (!window.confirm(`Delete "${channel.name}" and its cached videos?`)) return;
    try {
      await deleteVideoNewsChannel(channel._id, adminToken);
      toast.success("Deleted");
      fetchChannels();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleSync = async (channelId = null) => {
    try {
      setSyncing(true);
      const data = await syncVideoNewsNow(adminToken, channelId);
      const imported = (data.results || []).reduce((s, r) => s + (r.imported || 0), 0);
      toast.success(`Synced — ${imported} videos updated`);
      fetchChannels();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Video News</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Official YouTube news channels · {videoCount} cached videos
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Scheduler every {Math.round((scheduler?.refreshIntervalMs || 7200000) / 3600000)}h
            {scheduler?.lastRefreshTime
              ? ` · Last sync ${new Date(scheduler.lastRefreshTime).toLocaleString()}`
              : " · Waiting for first sync"}
            {" · "}
            {hasApiKey ? "YouTube Data API key configured" : "Using RSS fallback (set YOUTUBE_API_KEY for duration + richer metadata)"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleSync()}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:bg-gray-900 dark:text-red-300"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync Now
          </button>
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" />
            Add Channel
          </button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 md:grid-cols-2"
        >
          <div className="md:col-span-2 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {editingId ? "Edit Channel" : "New Channel"}
            </h2>
            <button type="button" onClick={resetForm} className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Channel name"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <input
            value={form.handle}
            onChange={(e) => setForm((p) => ({ ...p, handle: e.target.value }))}
            placeholder="Handle e.g. @BBCNews"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <input
            value={form.channelId}
            onChange={(e) => setForm((p) => ({ ...p, channelId: e.target.value }))}
            placeholder="YouTube channel ID (UC...)"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <input
            value={form.channelUrl}
            onChange={(e) => setForm((p) => ({ ...p, channelUrl: e.target.value }))}
            placeholder="Channel URL (optional)"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <select
            value={form.category}
            onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          >
            {(categories.length ? categories : ["Other"]).map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <input
            value={form.language}
            onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
            placeholder="Language (en/kn/hi)"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <input
            type="number"
            min={1}
            max={10}
            value={form.priority}
            onChange={(e) => setForm((p) => ({ ...p, priority: Number(e.target.value) }))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={resetForm} className="rounded-lg border px-3 py-2 text-xs font-semibold">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading channels…
          </div>
        ) : channels.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No channels yet. Restart backend to seed defaults.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {channels.map((channel) => (
              <li key={channel._id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                    <Youtube className="h-4 w-4 text-red-500" />
                    {channel.name}
                    <span className="text-xs font-medium text-gray-400">({channel.category})</span>
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {channel.handle || channel.channelId || channel.channelUrl || "—"}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {channel.isActive ? "Active" : "Disabled"}
                    {channel.lastFetchedAt ? ` · Last sync ${new Date(channel.lastFetchedAt).toLocaleString()}` : ""}
                    {channel.lastFetchError ? ` · Error: ${channel.lastFetchError}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleSync(channel._id)}
                    className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold dark:bg-gray-700"
                  >
                    Sync
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEdit(channel)}
                    className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold dark:bg-gray-700"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggle(channel)}
                    className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                  >
                    {channel.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(channel)}
                    className="rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 dark:bg-red-900/30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default VideoNewsDashboard;
